import type { Box } from "~/lib/svgEditor/selection";
import type { Point } from "~/lib/svgEditor/zoom";
import {
    apply,
    invert,
    type Matrix,
    scaleFactor,
} from "~/lib/svgEditor/matrix";
import { type PathCommand, readPathData } from "~/lib/svgEditor/pathData";

/** How far along one segment the walk goes at most, in points: a bound for a long curve close up. */
const MAX_SEGMENT_POINTS = 2048;

/** What the walk along an outline needs to know, and what it looks for. */
type Walk = {
    /** The part of the shape's own coordinates the points are wanted in; all of it without. */
    bounds: Box | undefined;
    /** What places the shape in the document; none when it lies there as it is. */
    matrix: Matrix | undefined;
    /** How far apart the points may lie, in the shape's own units. */
    spacing: number;
    /** What a point is looked for by, in the document's coordinates. */
    test: (point: Point) => boolean;
};

/**
 * Whether some point along the outline of the path data `data` — any of
 * its subpaths — passes `test`, in the document's coordinates, placed
 * there by `matrix`. The points are every segment's ends and, between
 * them, points at most `spacing` apart (in the document's units); the walk
 * ends at the first that passes. With `within`, only the parts of the
 * outline that may reach into that box (in the document's coordinates)
 * are walked along: a shape far larger than a lasso costs no more than its
 * part near it.
 *
 * The walk reads the path data itself, as it goes along it: the browser's
 * `getPointAtLength` walks the path from its start for every point, which
 * takes minutes for the large paths of a vectorized logo, and reading all
 * of a path first takes long where its first subpaths have the answer.
 */
export function someOutlinePoint(
    data: string,
    options: { matrix?: Matrix; spacing: number; within?: Box },
    test: (point: Point) => boolean,
): boolean {
    const { matrix, within } = options;
    const toLocal = matrix && invert(matrix);

    if (matrix && !toLocal) return false;

    const walk: Walk = {
        bounds: within && (toLocal ? boxThrough(toLocal, within) : within),
        matrix,
        spacing:
            Math.max(options.spacing, Number.EPSILON) /
            (matrix ? scaleFactor(matrix) : 1),
        test,
    };
    let point: Point = { x: 0, y: 0 };
    let start = point;
    let found = false;

    readPathData(data, (command) => {
        const to = endOf(command, start);

        found = segmentHas(walk, point, command, to);
        point = to;

        if (command.kind === "M") start = to;

        return found;
    });

    return found;
}

/** The angle from `u` to `v`, signed, in radians. */
function angleBetween(u: Point, v: Point): number {
    return Math.atan2(u.x * v.y - u.y * v.x, u.x * v.x + u.y * v.y);
}

/**
 * Whether a point along an elliptical arc from `from` to `to`, as SVG
 * draws it, passes — `from` is where the segment before ended: its centre
 * found from the ends, radii too small for them grown to fit. One without
 * a radius is a line.
 */
function arcHas(
    walk: Walk,
    from: Point,
    command: Extract<PathCommand, { kind: "A" }>,
    to: Point,
): boolean {
    if (from.x === to.x && from.y === to.y) return false;

    let rx = Math.abs(command.rx);
    let ry = Math.abs(command.ry);

    if (rx === 0 || ry === 0) return lineHas(walk, from, to);

    const phi = (command.rotation * Math.PI) / 180;
    const cos = Math.cos(phi);
    const sin = Math.sin(phi);
    const halfX = (from.x - to.x) / 2;
    const halfY = (from.y - to.y) / 2;
    const x1 = cos * halfX + sin * halfY;
    const y1 = -sin * halfX + cos * halfY;
    const lambda = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry);

    if (lambda > 1) {
        rx *= Math.sqrt(lambda);
        ry *= Math.sqrt(lambda);
    }

    const numerator = rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1;
    const denominator = rx * rx * y1 * y1 + ry * ry * x1 * x1;
    const root =
        (command.large === command.sweep ? -1 : 1) *
        Math.sqrt(Math.max(0, numerator / denominator));
    const centreX1 = (root * rx * y1) / ry;
    const centreY1 = (-root * ry * x1) / rx;
    const cx = cos * centreX1 - sin * centreY1 + (from.x + to.x) / 2;
    const cy = sin * centreX1 + cos * centreY1 + (from.y + to.y) / 2;
    const radius = Math.max(rx, ry);

    if (
        walk.bounds &&
        !overlaps(walk.bounds, {
            height: 2 * radius,
            width: 2 * radius,
            x: cx - radius,
            y: cy - radius,
        })
    ) {
        return false;
    }

    const startVector = { x: (x1 - centreX1) / rx, y: (y1 - centreY1) / ry };
    const endVector = { x: (-x1 - centreX1) / rx, y: (-y1 - centreY1) / ry };
    const theta = angleBetween({ x: 1, y: 0 }, startVector);
    let delta = angleBetween(startVector, endVector);

    if (!command.sweep && delta > 0) delta -= 2 * Math.PI;
    if (command.sweep && delta < 0) delta += 2 * Math.PI;

    const count = steps(radius * Math.abs(delta), walk.spacing);

    for (let index = 1; index <= count; index++) {
        const angle = theta + (delta * index) / count;
        const point = {
            x: cx + rx * cos * Math.cos(angle) - ry * sin * Math.sin(angle),
            y: cy + rx * sin * Math.cos(angle) + ry * cos * Math.sin(angle),
        };

        if (passes(walk, point)) return true;
    }

    return false;
}

/** The box around `box` taken through `matrix`: a box again, around its turned corners. */
function boxThrough(matrix: Matrix, box: Box): Box {
    const corners = [
        apply(matrix, { x: box.x, y: box.y }),
        apply(matrix, { x: box.x + box.width, y: box.y }),
        apply(matrix, { x: box.x, y: box.y + box.height }),
        apply(matrix, { x: box.x + box.width, y: box.y + box.height }),
    ];
    const xs = corners.map((corner) => corner.x);
    const ys = corners.map((corner) => corner.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);

    return { height: Math.max(...ys) - y, width: Math.max(...xs) - x, x, y };
}

/**
 * Whether a point along a quadratic or cubic Bézier curve, given by its
 * points from start to end, passes — its start is where the segment before
 * ended. The curve lies within the box of its points, so a curve whose box
 * misses the bounds is left out; it moves at most its degree times its
 * longest leg per unit of `t`, which sets how many points it takes.
 */
function curveHas(walk: Walk, points: readonly Point[]): boolean {
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    const left = Math.min(...xs);
    const top = Math.min(...ys);

    if (
        walk.bounds &&
        !overlaps(walk.bounds, {
            height: Math.max(...ys) - top,
            width: Math.max(...xs) - left,
            x: left,
            y: top,
        })
    ) {
        return false;
    }

    let leg = 0;

    for (let index = 1; index < points.length; index++) {
        leg = Math.max(
            leg,
            Math.hypot(
                points[index].x - points[index - 1].x,
                points[index].y - points[index - 1].y,
            ),
        );
    }

    const count = steps((points.length - 1) * leg, walk.spacing);

    for (let index = 1; index <= count; index++) {
        if (passes(walk, onCurve(points, index / count))) return true;
    }

    return false;
}

/** Where `command` ends; a close at the start of its subpath. */
function endOf(command: PathCommand, start: Point): Point {
    return command.kind === "Z" ? start : { x: command.x, y: command.y };
}

/**
 * Whether a point along the straight line from `from` to `to` passes —
 * looked for only along its part within the bounds, cut to them
 * (Liang–Barsky). `from` is where the segment before ended; it is looked
 * at again only where the bounds cut the line's start off.
 */
function lineHas(walk: Walk, from: Point, to: Point): boolean {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    let enter = 0;
    let leave = 1;

    if (walk.bounds) {
        const { bounds } = walk;
        const sides: [number, number][] = [
            [-dx, from.x - bounds.x],
            [dx, bounds.x + bounds.width - from.x],
            [-dy, from.y - bounds.y],
            [dy, bounds.y + bounds.height - from.y],
        ];

        for (const [outward, inside] of sides) {
            if (outward === 0) {
                if (inside < 0) return false;

                continue;
            }

            const at = inside / outward;

            if (outward < 0) enter = Math.max(enter, at);
            else leave = Math.min(leave, at);

            if (enter > leave) return false;
        }
    }

    const count = steps(Math.hypot(dx, dy) * (leave - enter), walk.spacing);

    for (let index = enter > 0 ? 0 : 1; index <= count; index++) {
        const at = enter + ((leave - enter) * index) / count;

        if (passes(walk, { x: from.x + dx * at, y: from.y + dy * at })) {
            return true;
        }
    }

    return false;
}

/** The point at `t` (0 to 1) along a quadratic or cubic Bézier curve. */
function onCurve(points: readonly Point[], t: number): Point {
    const u = 1 - t;

    if (points.length === 3) {
        const [p0, p1, p2] = points;

        return {
            x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
            y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
        };
    }

    const [p0, p1, p2, p3] = points;
    const uu = u * u;
    const tt = t * t;

    return {
        x:
            uu * u * p0.x +
            3 * uu * t * p1.x +
            3 * u * tt * p2.x +
            tt * t * p3.x,
        y:
            uu * u * p0.y +
            3 * uu * t * p1.y +
            3 * u * tt * p2.y +
            tt * t * p3.y,
    };
}

/** Whether two boxes share a point. */
function overlaps(first: Box, second: Box): boolean {
    return (
        first.x <= second.x + second.width &&
        second.x <= first.x + first.width &&
        first.y <= second.y + second.height &&
        second.y <= first.y + first.height
    );
}

/** Whether a point of the shape's own coordinates, in the bounds, passes the test in the document's. */
function passes(walk: Walk, point: Point): boolean {
    const { bounds } = walk;

    if (
        bounds &&
        (point.x < bounds.x ||
            point.x > bounds.x + bounds.width ||
            point.y < bounds.y ||
            point.y > bounds.y + bounds.height)
    ) {
        return false;
    }

    return walk.test(walk.matrix ? apply(walk.matrix, point) : point);
}

/** Whether a point along the segment `command` draws from `from` to `to` passes. */
function segmentHas(
    walk: Walk,
    from: Point,
    command: PathCommand,
    to: Point,
): boolean {
    switch (command.kind) {
        case "A":
            return arcHas(walk, from, command, to);

        case "C":
            return curveHas(walk, [
                from,
                { x: command.x1, y: command.y1 },
                { x: command.x2, y: command.y2 },
                to,
            ]);

        case "L":
        case "Z":
            return lineHas(walk, from, to);

        case "M":
            return passes(walk, to);

        case "Q":
            return curveHas(walk, [from, { x: command.x1, y: command.y1 }, to]);
    }
}

/** Into how many steps a way of `length` is cut, for its points to lie at most `spacing` apart. */
function steps(length: number, spacing: number): number {
    return Math.min(
        MAX_SEGMENT_POINTS,
        // A hair over a whole number of steps is that number: no extra
        // point for the noise of the arithmetic.
        Math.max(1, Math.ceil(length / spacing - 1e-9)),
    );
}
